import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  Download, FileSpreadsheet, FileText, RefreshCw, File, FileType, FileJson,
  Layers, MapPin, TrendingUp, Hash
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

applyPlugin(jsPDF);

const COLORS = ["#059669", "#10b981", "#0284c7", "#8B5CF6", "#F59E0B", "#EF4444", "#ec4899", "#14b8a6", "#f97316"];

const PlantaValoresTierra = () => {
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [columnFilters, setColumnFilters] = useState({ tipo: "", clasificacion: "", tcmmv: "", fichas: "" });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reportes/planta-valores-tierra`, { headers });
      setData(res.data);
    } catch {
      toast.error("Error al cargar reporte");
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const filteredFilas = (data?.filas || []).filter(f => {
    const cf = columnFilters;
    if (cf.tipo && !f.tipo?.toLowerCase().includes(cf.tipo.toLowerCase())) return false;
    if (cf.clasificacion && !f.clasificacion_terreno?.toLowerCase().includes(cf.clasificacion.toLowerCase())) return false;
    if (cf.tcmmv && !String(f.tcmmv_m2).includes(cf.tcmmv)) return false;
    if (cf.fichas && !String(f.fichas_asociadas).includes(cf.fichas)) return false;
    return true;
  });

  // Export functions
  const getExportData = () => filteredFilas.map(f => ({
    "Tipo": f.tipo, "Clasificacion del Terreno": f.clasificacion_terreno,
    "TCMMV/m2": f.tcmmv_m2, "Fichas Asociadas": f.fichas_asociadas,
    "Area Total (m2)": f.area_total_m2, "Valor Catastral Total": f.valor_catastral_total
  }));

  const exportToExcel = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos"); return; }
    const ws = XLSX.utils.json_to_sheet(d);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Planta Valores Tierra");
    XLSX.writeFile(wb, `planta_valores_tierra_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportToPDF = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos"); return; }
    const doc = new jsPDF('landscape');
    doc.setFontSize(16); doc.text("Reporte Planta de Valores de la Tierra", 14, 20);
    doc.setFontSize(10); doc.text(`Fecha: ${new Date().toLocaleDateString('es-VE')}`, 14, 28);
    doc.autoTable({ head: [Object.keys(d[0])], body: d.map(r => Object.values(r)), startY: 35, styles: { fontSize: 8 }, headStyles: { fillColor: [5, 150, 105] } });
    doc.save(`planta_valores_tierra_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportToCSV = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos"); return; }
    const h = Object.keys(d[0]).join(",");
    const rows = d.map(r => Object.values(r).map(v => `"${v}"`).join(",")).join("\n");
    saveAs(new Blob(["\ufeff" + `${h}\n${rows}`], { type: "text/csv;charset=utf-8" }), `planta_valores_tierra_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const exportToJSON = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos"); return; }
    saveAs(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }), `planta_valores_tierra_${new Date().toISOString().split('T')[0]}.json`);
    toast.success("Exportado a JSON");
  };

  const resumen = data?.resumen || {};

  return (
    <div className="space-y-6" data-testid="reporte-pvt">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Clasificaciones</p>
                <p className="text-2xl font-bold text-slate-900">{resumen.total_clasificaciones || 0}</p>
              </div>
              <Layers className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Fichas Asociadas</p>
                <p className="text-2xl font-bold text-slate-900">{resumen.total_fichas_asociadas || 0}</p>
              </div>
              <MapPin className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">TCMMV/m2 Maximo</p>
                <p className="text-2xl font-bold text-slate-900">{resumen.valor_maximo_tcmmv || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Promedio TCMMV/m2</p>
                <p className="text-2xl font-bold text-slate-900">{resumen.promedio_tcmmv || 0}</p>
              </div>
              <Hash className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - TCMMV by Type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">TCMMV/m2 por Tipo de Terreno</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.grafico_barras?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.grafico_barras}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="tipo" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="tcmmv_m2" name="TCMMV/m2" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">Sin datos</div>
            )}
          </CardContent>
        </Card>

        {/* Pie Chart - Fichas by Classification */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Fichas Catastrales por Clasificacion</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.grafico_pie?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={data.grafico_pie} cx="50%" cy="50%" outerRadius={100} dataKey="fichas" nameKey="tipo" label={({ tipo, fichas }) => `${tipo}: ${fichas}`}>
                    {data.grafico_pie.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">Sin fichas asociadas</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Detalle por Clasificacion del Terreno</CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{filteredFilas.length} de {data?.filas?.length || 0} registros</span>
              <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Actualizar
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" />Exportar</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToExcel}><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToPDF}><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToCSV}><File className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToJSON}><FileJson className="w-4 h-4 mr-2" />JSON</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Tipo</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Clasificacion del Terreno</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">TCMMV/m2</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Fichas Asociadas</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Area Total (m2)</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Valor Catastral Total</th>
                  </tr>
                  <tr className="border-b">
                    <th className="px-4 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.tipo} onChange={e => setColumnFilters({...columnFilters, tipo: e.target.value})} /></th>
                    <th className="px-4 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.clasificacion} onChange={e => setColumnFilters({...columnFilters, clasificacion: e.target.value})} /></th>
                    <th className="px-4 py-1"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={columnFilters.tcmmv} onChange={e => setColumnFilters({...columnFilters, tcmmv: e.target.value})} /></th>
                    <th className="px-4 py-1"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={columnFilters.fichas} onChange={e => setColumnFilters({...columnFilters, fichas: e.target.value})} /></th>
                    <th className="px-4 py-1"></th>
                    <th className="px-4 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFilas.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No hay registros</td></tr>
                  ) : filteredFilas.map((f, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50" data-testid={`row-${i}`}>
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">{f.tipo}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{f.clasificacion_terreno}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-700">{f.tcmmv_m2?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${f.fichas_asociadas > 0 ? "text-blue-600" : "text-slate-400"}`}>{f.fichas_asociadas}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{f.area_total_m2?.toLocaleString("es-VE", { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900">{f.valor_catastral_total?.toLocaleString("es-VE", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
                {filteredFilas.length > 0 && (
                  <tfoot>
                    <tr className="bg-emerald-50 font-medium border-t-2 border-emerald-200">
                      <td colSpan={3} className="px-4 py-3 text-right text-emerald-800">Totales:</td>
                      <td className="px-4 py-3 text-right text-emerald-800">{filteredFilas.reduce((a, f) => a + f.fichas_asociadas, 0)}</td>
                      <td className="px-4 py-3 text-right text-emerald-800">{filteredFilas.reduce((a, f) => a + f.area_total_m2, 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3 text-right text-emerald-800">{filteredFilas.reduce((a, f) => a + f.valor_catastral_total, 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlantaValoresTierra;
